import { ReactNode, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { McpIcon } from '@/components/atoms/Icons/McpIcon';
import useMcpServers from '@/components/pages/MCPs/hooks/useMcpServers';
import { selectAccountSlug } from '@/redux/session/selectors';
import type { ToolInvocation } from '@/shared/types/tryIt';

import { DetailRowSpec, ExpandToggle, NewTabStepLink, PlainRow } from './DetailsRow';
import { OutcomeBadge, outcomeIconClass } from './StepRows';
import { selectBrainId } from '../../../redux/selectors';
import { ConversationTurn } from '../../../types/conversation';

export const keyToolCalls = (invocations: ToolInvocation[]) =>
  invocations.map((invocation, index) => ({
    key: `${invocation.mcp_id}-${invocation.name}-${index}`,
    invocation,
  }));

const NAME_SLOT = '\u0000';

const SYNTHETIC_SERVER_KEY: Record<string, string> = {
  '004facab-37b4-5aa5-957b-4fd4ed112d0a': 'mcp_tools.004facab-37b4-5aa5-957b-4fd4ed112d0a',
  '1b3ad188-3c08-5eea-970d-0a49430fb31f': 'mcp_tools.1b3ad188-3c08-5eea-970d-0a49430fb31f',
  'dc8d655a-ae4b-5552-92da-c90cc14be729': 'mcp_tools.dc8d655a-ae4b-5552-92da-c90cc14be729',
  'feb8e1ef-e9e0-526b-9a3d-8b3f9ae2a5fc': 'mcp_tools.feb8e1ef-e9e0-526b-9a3d-8b3f9ae2a5fc',
};

const MOVEO_SERVER_IDS = new Set([
  '1b3ad188-3c08-5eea-970d-0a49430fb31f',
  'feb8e1ef-e9e0-526b-9a3d-8b3f9ae2a5fc',
]);

type ToolRowProps = { invocation: ToolInvocation };

const ToolLink = ({ mcpId, children }: { mcpId: string; children: ReactNode }) => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const brainId = useSelector(selectBrainId);
  const hasPage = Boolean(slug && brainId) && !SYNTHETIC_SERVER_KEY[mcpId];

  return (
    <NewTabStepLink
      to={hasPage ? `/${slug}/ai-agents/${brainId}/mcps/${mcpId}` : null}
      tooltip={t('try_it.details.go_to_tool')}
    >
      {children}
    </NewTabStepLink>
  );
};

export const ToolRow = ({ invocation }: ToolRowProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const brainId = useSelector(selectBrainId);
  const { mcpServers } = useMcpServers(brainId);

  const failed = !invocation.succeeded;
  const server = mcpServers.find((candidate) => candidate.mcp_id === invocation.mcp_id);
  const syntheticKey = SYNTHETIC_SERVER_KEY[invocation.mcp_id];
  const sourceName = server?.name ?? (syntheticKey ? t(syntheticKey) : invocation.mcp_id);
  const [before, after = ''] = t('try_it.details.tool_invocation', { 0: NAME_SLOT }).split(
    NAME_SLOT
  );
  const [beforeSource, afterSource = ''] = t('try_it.details.from', { 0: NAME_SLOT }).split(
    NAME_SLOT
  );
  const payload = expanded
    ? JSON.stringify(
        { arguments: invocation.arguments, result: invocation.result, meta: invocation.meta },
        null,
        2
      )
    : '';

  return (
    <div className="flex flex-col items-start gap-3 self-stretch">
      <div className="flex items-start justify-between gap-3 self-stretch">
        <span className="min-w-0 flex-1">
          <PlainRow>
            {before}
            <ToolLink mcpId={invocation.mcp_id}>{invocation.name}</ToolLink>
            {after}
          </PlainRow>
        </span>
        <ExpandToggle
          expanded={expanded}
          onToggle={() => setExpanded((value) => !value)}
          label={t('common.show_more')}
        />
      </div>

      {expanded && (
        <div className="flex flex-col items-start gap-2 self-stretch pb-2">
          <div className="flex items-center gap-1.5">
            <OutcomeBadge flavour={failed ? 'destructive' : 'successful'}>
              {failed ? t('try_it.details.failed') : t('try_it.details.passed')}
            </OutcomeBadge>
            {typeof invocation.duration_ms === 'number' && (
              <OutcomeBadge className="border-border">
                {t('try_it.details.execution_time', { 0: Math.round(invocation.duration_ms) })}
              </OutcomeBadge>
            )}
          </div>

          <div className="flex items-center self-stretch rounded-md border border-stroke bg-bg-muted px-4 py-2">
            <span className="text-sm leading-5 text-foreground">
              <span className="font-medium">{invocation.name}</span> {beforeSource}
              <span className="font-medium">{sourceName}</span>
              {afterSource}
            </span>
          </div>

          <div className="flex flex-col items-start gap-4 self-stretch rounded-md border border-stroke bg-background p-4">
            <pre className="w-full overflow-x-auto text-xs leading-5 text-foreground-muted">
              {payload}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

export type ToolSource = 'agent' | 'moveo';

const sourceOf = (invocation: ToolInvocation): ToolSource =>
  MOVEO_SERVER_IDS.has(invocation.mcp_id) ? 'moveo' : 'agent';

export const buildToolRows = (turn: ConversationTurn, source?: ToolSource): DetailRowSpec[] =>
  keyToolCalls(turn.metadata.toolCalls ?? [])
    .filter(({ invocation }) => !source || sourceOf(invocation) === source)
    .map(({ key, invocation }) => ({
      key,
      icon: (
        <span className={outcomeIconClass(invocation.succeeded)}>
          <McpIcon size={16} />
        </span>
      ),
      head: <ToolRow invocation={invocation} />,
    }));
